import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import InventoryIcon from "@mui/icons-material/Inventory";
import StoreIcon from "@mui/icons-material/Store";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import CategoryIcon from "@mui/icons-material/Category";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LockIcon from "@mui/icons-material/Lock";

import {
  inventoryManagementUrl,
  salesManagement,
  companyManagementUrl,
} from "../../../config";
import DynamicTable from "../../../data/DynamicTable/dynamicTable";
import {
  fetchDataEngine,
  graphqlQuery,
} from "../../../data/Axios/DynamicService";
import {
  AllRetailDistribution,
  SearchRetailDistribution,
  GetRetailDistributionStats,
} from "../../../data/Axios/queries";
import { format } from "date-fns";

const base_url = inventoryManagementUrl.uri;
const company_baseurl = companyManagementUrl.uri;

const actions = {
  entity: "Retail Distributed Logs",
  edit: {
    key: "EDIT",
    button_name: "Edit",
    Show_Button: false,
    permission: "EditPermission",
  },
  showStatus: true,
  add: {
    key: "ADD",
    button_name: "Add",
    Show_Button: false,
    permission: "CreatePermission",
  },
  activateDeactivate: {
    key: "ACTIVATEDEACTIVATE",
    button_name: "Deactivate",
    Show_Button: false,
    permission: "ActivatePermission",
    modelName: "inventoryIds",
    inputObjectName: "bulkActivateDeactivateInventory",
  },
  AssignTask: {
    key: "ASSIGN_TASK",
    button_name: "Assign Task",
    Show_Button: false,
    permission: "hasNoPermission",
    roleGroupId: [1, 2],
  },
  showMore: {
    key: "SHOWMORE",
    button_name: "Show More",
    Show_Button: true,
    permission: "hasNoPermission",
    roleGroupId: [1, 2, 3, 4],
    title: "Distribution Details",
  },
};

function RetailDistributionTable() {
  // ✅ Read token once at the top
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const branchId = parseInt(decodedToken?.BranchId);

  // ✅ User is branch-locked when branchId is a valid positive number
  const isBranchLocked = !isNaN(branchId) && branchId > 0;


  const [distributions, setDistributions] = useState([]);
  const [filteredDistributions, setFilteredDistributions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Filter states
  const [branches, setBranches] = useState([]);

  // ✅ Default selectedBranch to locked branchId if applicable
  const [selectedBranch, setSelectedBranch] = useState(
    isBranchLocked ? branchId : ""
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedColor, setSelectedColor] = useState("");
  const [colors, setColors] = useState([]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);

  // Statistics
  const [stats, setStats] = useState({
    totalDistributions: 0,
    totalQuantity: 0,
    uniqueProducts: 0,
    uniqueBranches: 0,
    todayDistributions: 0,
    weekDistributions: 0,
  });

  // Theme colors
  const theme = {
    primary: "#1976D2",
    secondary: "#0288D1",
    success: "#4CAF50",
    warning: "#FF9800",
    error: "#F44336",
    lightBg: "#E3F2FD",
    cardBg: "#FFFFFF",
    purple: "#9C27B0",
    lockedBg: "#F0F7FF",
  };

  const ignore = ["averageCost", "createdBy", "productCategory", "cartons"];

  // ✅ Fetch branches only when user is NOT branch-locked
  const fetchBranches = useCallback(async () => {
    try {
      const payload = { isWarehouse: 0 };
      const returnFields = ["id", "branchName"];
      const res = await fetchDataEngine(
        "allBranchesByColumnValues",
        "branchModel",
        payload,
        returnFields,
        company_baseurl
      );
      setBranches(res || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  }, [isBranchLocked]);

  // ✅ Calculate statistics from local data
  const calculateStats = useCallback((data) => {
    const totalDistributions = data.length;
    const totalQuantity = data.reduce(
      (sum, item) => sum + (item.quantity || 0),
      0
    );
    const uniqueProducts = new Set(data.map((item) => item.productId)).size;
    const uniqueBranches = new Set(data.map((item) => item.branchId)).size;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const todayDistributions = data.filter((item) => {
      const itemDate = new Date(item.createdDate);
      itemDate.setHours(0, 0, 0, 0);
      return itemDate.getTime() === today.getTime();
    }).length;

    const weekDistributions = data.filter((item) => {
      const itemDate = new Date(item.createdDate);
      return itemDate >= weekAgo;
    }).length;

    setStats({
      totalDistributions,
      totalQuantity,
      uniqueProducts,
      uniqueBranches,
      todayDistributions,
      weekDistributions,
    });
  }, []);

  // ✅ Fetch stats from server
  const fetchStats = useCallback(async () => {
    try {
      const query = GetRetailDistributionStats(selectedBranch || 0);
      const result = await graphqlQuery(query, base_url);

      const statsArray = Array.isArray(result)
        ? result
        : result?.retailDistributionStats || [];

      if (statsArray && statsArray.length > 0) {
        const statsData = statsArray[0];
        setStats({
          totalDistributions: statsData.totalDistributions || 0,
          totalQuantity: statsData.totalQuantity || 0,
          uniqueProducts: statsData.uniqueProducts || 0,
          uniqueBranches: statsData.uniqueBranches || 0,
          todayDistributions: statsData.todayDistributions || 0,
          weekDistributions: statsData.weekDistributions || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
      setStats({
        totalDistributions: 0,
        totalQuantity: 0,
        uniqueProducts: 0,
        uniqueBranches: 0,
        todayDistributions: 0,
        weekDistributions: 0,
      });
    }
  }, [selectedBranch]);

  // ✅ Fetch distribution data
  const fetchDistributions = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const query = SearchRetailDistribution({
        branchId: selectedBranch,
        search: searchQuery || "",
        limit: pageSize,
        pageNumber: page + 1,
      });
      const result = await graphqlQuery(query, base_url);

      const connection = result || {};
      const rows = connection.items || (Array.isArray(result) ? result : []);
      const totalCount = connection.totalCount || rows.length;

      if (rows && rows.length > 0) {
        const distributionData = rows.map((item) => ({
          ...item,
          formattedDate: item.createdDate
            ? format(new Date(item.createdDate), "yyyy-MM-dd HH:mm")
            : "",
        }));

        setDistributions(distributionData);
        setRowCount(totalCount);

        const uniqueColors = [
          ...new Set(
            distributionData
              .map((item) => item.colorName)
              .filter((color) => color)
          ),
        ];
        setColors(uniqueColors);
        setFilteredDistributions(distributionData);
      } else {
        setDistributions([]);
        setFilteredDistributions([]);
        setRowCount(0);
        setColors([]);
      }
    } catch (error) {
      console.error("Error fetching distributions:", error);
      setError("Failed to load distribution data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, searchQuery, pageSize, page]);

    // ✅ Branch display name from token (fallback to ID)
  const lockedBranchName =
  isBranchLocked
      ? `${branches.find((b) => b.id === parseInt(branchId)) ?.branchName }`
      : branches.find((b) => b.id === selectedBranch )?.branchName || "Unknown";

  // ✅ Apply client-side filters (date + color)
  const applyFilters = useCallback(
    (data, branch, search, date, color) => {
      let filtered = [...data];

      if (branch) {
        filtered = filtered.filter(
          (item) =>
            item.branchId === branch || item.branchId === String(branch)
        );
      }

      if (search) {
        const searchLower = search.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            item.productName?.toLowerCase().includes(searchLower) ||
            item.productCode?.toLowerCase().includes(searchLower) ||
            item.branchName?.toLowerCase().includes(searchLower) ||
            item.colorName?.toLowerCase().includes(searchLower)
        );
      }

      if (date !== "all") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (date === "today") {
          filtered = filtered.filter((item) => {
            const itemDate = new Date(item.createdDate);
            itemDate.setHours(0, 0, 0, 0);
            return itemDate.getTime() === today.getTime();
          });
        } else if (date === "week") {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          filtered = filtered.filter((item) => {
            const itemDate = new Date(item.createdDate);
            return itemDate >= weekAgo;
          });
        } else if (date === "month") {
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          filtered = filtered.filter((item) => {
            const itemDate = new Date(item.createdDate);
            return itemDate >= monthAgo;
          });
        }
      }

      if (color) {
        filtered = filtered.filter((item) => item.colorName === color);
      }

      setFilteredDistributions(filtered);
      calculateStats(filtered);
    },
    [calculateStats]
  );

  // ✅ Reset page when search or branch changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery, selectedBranch]);

  // ✅ Fetch data when search, branch, page, or pageSize changes (debounced)
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchDistributions();
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery, selectedBranch, page, pageSize]);

  // ✅ Fetch stats on mount and when branch changes
  useEffect(() => {
    fetchBranches();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [selectedBranch]);

  // ✅ Apply client-side filters when date/color changes
  useEffect(() => {
    if (distributions.length > 0) {
      applyFilters(distributions, selectedBranch, "", dateFilter, selectedColor);
    }
  }, [dateFilter, selectedColor, distributions, selectedBranch, applyFilters]);

  // ✅ Handle refresh
  const handleRefresh = () => {
    fetchStats();
    fetchDistributions();
  };

  // ✅ Clear filters — never resets branch if user is branch-locked
  const handleClearFilters = () => {
    if (!isBranchLocked) setSelectedBranch("");
    setSearchQuery("");
    setDateFilter("all");
    setSelectedColor("");
  };

  // ✅ Active filters — branch excluded from count when locked
  const hasActiveFilters = Boolean(
    (!isBranchLocked && selectedBranch) ||
      searchQuery ||
      dateFilter !== "all" ||
      selectedColor
  );

  return (
    <Box sx={{ p: 3, bgcolor: "#F5F7FA", minHeight: "100vh" }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <LocalShippingIcon
            sx={{ fontSize: 32, color: theme.primary, mr: 1 }}
          />
          <Typography variant="h4" fontWeight="700" color={theme.primary}>
            Retail Distribution Logs
          </Typography>
          
        </Box>
        <Typography variant="body2" color="text.secondary">
          {isBranchLocked
            ? `Showing distribution logs for your branch: ${lockedBranchName}`
            : "Track and monitor all retail distribution activities"}
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Total Distributions Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
              color: "white",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                width: "150px",
                height: "150px",
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "50%",
                top: "-50px",
                right: "-50px",
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <LocalShippingIcon sx={{ fontSize: 28, mr: 1 }} />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Total Distributions
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="700">
                {stats.totalDistributions.toLocaleString()}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, mt: 1 }}>
                {isBranchLocked
                  ? lockedBranchName
                  : hasActiveFilters
                  ? "Filtered View"
                  : "All Records"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Quantity Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Total Pairs Distributed
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="700"
                    color={theme.primary}
                  >
                    {stats.totalQuantity.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isBranchLocked ? lockedBranchName : "Across all branches"}
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: theme.lightBg, p: 1.5, borderRadius: 2 }}>
                  <InventoryIcon
                    sx={{ color: theme.primary, fontSize: 28 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Unique Products Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Unique Products
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="700"
                    color={theme.success}
                  >
                    {stats.uniqueProducts}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stats.uniqueBranches} branches
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: "#E8F5E9", p: 1.5, borderRadius: 2 }}>
                  <CategoryIcon sx={{ color: theme.success, fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Recent Activity
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      mt: 1,
                      alignItems: "baseline",
                    }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight="700"
                      color={theme.warning}
                    >
                      {stats.todayDistributions}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      today
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {stats.weekDistributions} this week
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: "#FFF3E0", p: 1.5, borderRadius: 2 }}>
                  <CalendarTodayIcon
                    sx={{ color: theme.warning, fontSize: 28 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter and Actions Section */}
      <Paper
        elevation={0}
        sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: theme.cardBg }}
      >
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <FilterListIcon sx={{ color: theme.primary }} />
          <Typography variant="h6" fontWeight="600">
            Filters
          </Typography>
          {hasActiveFilters && (
            <Chip
              label={`${
                [
                  !isBranchLocked && selectedBranch,
                  searchQuery,
                  dateFilter !== "all",
                  selectedColor,
                ].filter(Boolean).length
              } Active`}
              size="small"
              color="primary"
              onDelete={handleClearFilters}
            />
          )}
        </Box>

        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by product, code, branch, or color..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery("")}
                        edge="end"
                      >
                        <Typography variant="caption">×</Typography>
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>

          {/* Branch Filter — locked indicator OR dropdown */}
          {isBranchLocked ? (
            // ✅ Read-only locked branch display
            <Grid item xs={12} sm={6} md={2.5}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: theme.lockedBg,
                  height: 40,
                }}
              >
                <LockIcon sx={{ fontSize: 16, color: theme.primary }} />
                <StoreIcon sx={{ fontSize: 16, color: theme.primary }} />
                <Typography
                  variant="body2"
                  color={theme.primary}
                  fontWeight="600"
                  noWrap
                >
                  {lockedBranchName}
                </Typography>
              </Box>
            </Grid>
          ) : (
            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select
                  value={selectedBranch}
                  label="Branch"
                  onChange={(e) => setSelectedBranch(e.target.value)}
                >
                  <MenuItem value="">
                    <em>All Branches</em>
                  </MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <StoreIcon
                          sx={{ fontSize: 18, color: theme.primary }}
                        />
                        {branch.branchName}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Date Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateFilter}
                label="Date Range"
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarTodayIcon
                      sx={{ fontSize: 18, color: theme.success }}
                    />
                    Today
                  </Box>
                </MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Color Filter */}
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Color</InputLabel>
              <Select
                value={selectedColor}
                label="Color"
                onChange={(e) => setSelectedColor(e.target.value)}
              >
                <MenuItem value="">
                  <em>All Colors</em>
                </MenuItem>
                {colors.map((color) => (
                  <MenuItem key={color} value={color}>
                    {color}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Actions */}
          <Grid item xs={12} md={1.5}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{
                    bgcolor: theme.lightBg,
                    "&:hover": { bgcolor: theme.primary, color: "white" },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {/* ✅ Branch chip only shown when NOT locked */}
            {!isBranchLocked && selectedBranch && (
              <Chip
                label={`Branch: ${
                  branches.find((b) => b.id === selectedBranch)?.branchName ||
                  "Unknown"
                }`}
                size="small"
                onDelete={() => setSelectedBranch("")}
                color="primary"
                variant="outlined"
              />
            )}
            {searchQuery && (
              <Chip
                label={`Search: "${searchQuery}"`}
                size="small"
                onDelete={() => setSearchQuery("")}
                color="primary"
                variant="outlined"
              />
            )}
            {dateFilter !== "all" && (
              <Chip
                label={`Date: ${
                  dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1)
                }`}
                size="small"
                onDelete={() => setDateFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {selectedColor && (
              <Chip
                label={`Color: ${selectedColor}`}
                size="small"
                onDelete={() => setSelectedColor("")}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Dynamic Table */}
      <Paper
        elevation={0}
        sx={{ borderRadius: 2, overflow: "hidden", bgcolor: theme.cardBg }}
      >
        {loading && filteredDistributions.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DynamicTable
            title="Distribution Logs"
            subtitle={`Showing ${filteredDistributions.length} of ${rowCount} distributions${
              hasActiveFilters ? " (filtered)" : ""
            }`}
            query={AllRetailDistribution}
            base_url={base_url}
            actions={actions}
            ignoreFields={ignore}
            rows={filteredDistributions}
            rowCount={rowCount}
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            paginationMode="server"
            pagination={true}
          />
        )}
      </Paper>
    </Box>
  );
}

export default RetailDistributionTable;